import { useContext, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { toast } from 'sonner';

import { BrokenContainerReportsCard } from '@/components/BrokenContainerReports/BrokenContainerReportsCard';
import type { BrokenContainerLineData } from '@/components/BrokenContainerReports/brokenContainerReportFormat';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useClearBrokenContainerMutation } from '@/graphql/mutations/clearBrokenContainer.generated';
import { useContainerBrokenReportsQuery } from '@/graphql/queries/containerBrokenReports.generated';
import {
  CanAccessOperationsData,
  ViewerContext,
} from '@/providers/ViewerContextProvider';
import i18n from '@/utils/i18n';

interface ContainerBrokenReportsProps {
  containerId: string;
  onCleared: () => void;
}

export function ContainerBrokenReports({
  containerId,
  onCleared,
}: ContainerBrokenReportsProps) {
  const viewer = useContext(ViewerContext);
  const canClear = CanAccessOperationsData(viewer);
  const [lineToClear, setLineToClear] = useState<BrokenContainerLineData>();

  const { data, error, refetch } = useContainerBrokenReportsQuery({
    variables: { containerId },
    fetchPolicy: 'cache-and-network',
  });
  const [clearBrokenContainer, { loading: clearing }] =
    useClearBrokenContainerMutation();

  const reports = data?.containerBrokenReports ?? [];

  const handleClear = async () => {
    if (!lineToClear) return;
    try {
      await clearBrokenContainer({ variables: { id: lineToClear.id } });
      toast.success(i18n.t('BrokenContainers:toastCleared'));
    } catch (clearError) {
      if (
        clearError instanceof ApolloError &&
        clearError.graphQLErrors.some(
          (e) => e.message === 'BROKEN_CONTAINER_NOT_CLEARABLE',
        )
      ) {
        toast.info(i18n.t('BrokenContainers:toastAlreadyCleared'));
      } else {
        toast.error(i18n.t('BrokenContainers:toastClearError'));
        return;
      }
    }
    setLineToClear(undefined);
    await refetch().catch(() => null);
    onCleared();
  };

  if (error && !data) {
    return (
      <p className="tw:mb-4 tw:text-sm tw:text-muted-foreground">
        {i18n.t('BrokenContainers:errorLoading')}
      </p>
    );
  }
  if (reports.length === 0) return null;

  return (
    <>
      <BrokenContainerReportsCard
        title={i18n.t('BrokenContainers:containerCardTitle')}
        description={i18n.t('BrokenContainers:containerCardDescription')}
        reports={reports}
        linesOf={(report) =>
          report.lines.filter((line) => line.container?.id === containerId)
        }
        showOrigin
        onClearLine={canClear ? setLineToClear : undefined}
        className="tw:mb-4"
      />

      <Dialog
        open={!!lineToClear}
        onOpenChange={(open) => !open && !clearing && setLineToClear(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {i18n.t('BrokenContainers:clearDialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {i18n.t('BrokenContainers:clearDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <ul className="tw:list-disc tw:pl-5 tw:text-sm tw:flex tw:flex-col tw:gap-1">
              <li>{i18n.t('BrokenContainers:clearDialogCheckout')}</li>
              <li>{i18n.t('BrokenContainers:clearDialogInStock')}</li>
            </ul>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={clearing}>
                {i18n.t('Common:cancel')}
              </Button>
            </DialogClose>
            <Button onClick={handleClear} disabled={clearing}>
              {clearing && <Spinner />}
              {i18n.t('BrokenContainers:clearConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
